import { type FormEventHandler, useState } from 'react';
import { useDispatch } from '@witmetrics/data-store';
import { UnauthorizedError } from '@witmetrics/utils/errors';
import { isBlank } from '@witmetrics/utils/strings';
import { showErrorToaster } from '@/utils/toasters';
import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import TextField from '@/components/inputs/TextField';
import Button from '@/components/Button';
import TextLink from '@/components/TextLink';
import { classes, loginUser } from './utils';

export type LoginFormProps = {
  onForgotPassword: () => void;
};

export default function LoginForm({ onForgotPassword }: LoginFormProps) {
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoggingIn, toggleLoggingIn] = useToggle(false);

  const isEnabled = !isBlank(email) && !isBlank(password);

  const handleSubmit: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    handleLogin();
  };

  const handleLogin = async () => {
    if (!isEnabled || isLoggingIn) return;
    try {
      toggleLoggingIn(true);
      const user = await loginUser(email, password);
      toggleLoggingIn(false);
      dispatch.addCurrentUser(user);
    } catch (err) {
      toggleLoggingIn(false);
      if (err instanceof UnauthorizedError) {
        showErrorToaster('Invalid user credentials');
      } else {
        onApiError(err, 'Error logging in');
      }
    }
  };

  return (
    <form name="login" className={classes.wrapper} onSubmit={handleSubmit}>
      <TextField
        autoFocus
        className={classes.email}
        name="email"
        type="email"
        label="Email *"
        autoComplete="email"
        value={email}
        onChange={setEmail}
      />
      <TextField
        className={classes.password}
        name="password"
        type="password"
        label="Password *"
        autoComplete="current-password"
        value={password}
        onChange={setPassword}
      />
      <Button
        busy={isLoggingIn}
        disabled={!isEnabled}
        busyLabel="Signing in"
        type="submit">
        Sign in
      </Button>
      <div className={classes.forgotWrapper}>
        <TextLink onClick={onForgotPassword}>Forgot password</TextLink>
      </div>
    </form>
  );
}
